import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../../services/api';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import './Products.css';
import './ProductsActions.css';
import './AllSellStyles.css';
import './MobileResponsive.css';

const statusMap = {
  ordered: { label: 'Ordered', className: 'badge badge-info' },
  partial: { label: 'Partial', className: 'badge badge-warning' },
  completed: { label: 'Completed', className: 'badge badge-success' },
};

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
};

const PurchaseRequisition = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [perPage, setPerPage] = useState(25);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [error, setError] = useState('');

  const [filters, setFilters] = useState({
    location_id: '',
    status: '',
    start_date: '',
    end_date: '',
    required_by_start: '',
    required_by_end: '',
  });
  const [locations, setLocations] = useState([]);

  const [viewItem, setViewItem] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [openActionId, setOpenActionId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: null, bottom: null, left: 0, maxHeight: null });

  useEffect(() => {
    if (!openActionId) return;
    const handleClickOutside = (e) => {
      if (e.target.closest('.action-btn-trigger') || e.target.closest('.action-dropdown-menu')) return;
      setOpenActionId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionId]);

  const toggleDropdown = (rowId, event) => {
    event.stopPropagation();
    if (openActionId === rowId) {
      setOpenActionId(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      const gap = 8;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const menuMinWidth = 220;
      const edgePadding = 8;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      const openUpwards = spaceBelow < 240 && spaceAbove > spaceBelow;
      const availableHeight = openUpwards ? (spaceAbove - gap - edgePadding) : (spaceBelow - gap - edgePadding);
      const maxHeight = Math.max(160, Math.min(Math.floor(viewportHeight * 0.6), Math.floor(availableHeight)));

      const left = Math.min(
        Math.max(rect.left, edgePadding),
        Math.max(edgePadding, viewportWidth - menuMinWidth - edgePadding)
      );

      setDropdownPosition({
        top: openUpwards ? null : (rect.bottom + gap),
        bottom: openUpwards ? (viewportHeight - rect.top + gap) : null,
        left,
        maxHeight
      });
      setOpenActionId(rowId);
    }
  };

  const fetchData = async (page = 1) => {
    setIsLoading(true);
    setError('');
    try {
      const params = {
        page,
        per_page: perPage,
        search: searchTerm,
      };
      if (filters.location_id) params.location_id = filters.location_id;
      if (filters.status) params.status = filters.status;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.required_by_start) params.required_by_start = filters.required_by_start;
      if (filters.required_by_end) params.required_by_end = filters.required_by_end;

      const result = await apiService.getPurchaseRequisitions(params);
      if (result.success) {
        setData(result.data || []);
        setTotal(result.total || 0);
        setCurrentPage(result.current_page || 1);
        setLastPage(result.last_page || 1);
      } else {
        setError(result.message || 'Failed to load purchase requisitions');
      }
    } catch (err) {
      setError(err.message || 'Failed to load purchase requisitions');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await apiService.getLocations();
      const list = Array.isArray(res) ? res : (res.data || []);
      setLocations(list);
    } catch (err) {
      /* console.error('Error fetching locations:', err); */
    }
  };

  useEffect(() => {
    fetchLocations();
    fetchData(1);
  }, []);

  useEffect(() => {
    fetchData(1);
  }, [perPage, filters]);

  useEffect(() => {
    const timer = setTimeout(() => fetchData(1), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      location_id: '',
      status: '',
      start_date: '',
      end_date: '',
      required_by_start: '',
      required_by_end: '',
    });
    setSearchTerm('');
  };

  const handleExport = (format) => {
    if (data.length === 0) {
      alert('No data available to export');
      setShowExportDropdown(false);
      return;
    }

    const headers = ['Requisition No', 'Date', 'Delivery Date', 'Requested By', 'Location', 'Status'];
    const rows = data.map((r) => [
      r.ref_no || '',
      formatDate(r.transaction_date),
      formatDate(r.delivery_date),
      r.added_by || '',
      r.location_name || '',
      r.status || '',
    ]);

    const title = 'Purchase Requisitions';
    const filename = 'purchase_requisitions';

    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();

    setShowExportDropdown(false);
  };

  const handleDelete = async (id) => {
    setOpenActionId(null);
    if (!window.confirm('Are you sure you want to delete this purchase requisition?')) return;
    try {
      const result = await apiService.deletePurchaseRequisition(id);
      if (result && result.success !== false) {
        toast.success('Purchase requisition deleted successfully.');
        fetchData(currentPage);
      } else {
        toast.error(result?.message || 'Failed to delete purchase requisition');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to delete purchase requisition');
    }
  };

  const handleView = async (id) => {
    setOpenActionId(null);
    setViewLoading(true);
    setViewItem(null);
    try {
      const result = await apiService.getPurchaseRequisition(id);
      if (result && result.success !== false) {
        setViewItem(result.data || result);
      } else {
        toast.error(result?.message || 'Failed to load requisition details');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load requisition details');
    } finally {
      setViewLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > lastPage) return;
    fetchData(page);
  };

  return (
    <div className="products-container">
      <div className="main-card">

        <div className="table-controls">
          <div className="table-controls-left">
            <div className="entries-dropdown">
              <span>Show</span>
              <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>
          <div className="table-controls-right">
            <div className="tabs-search-wrapper">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="tabs-search-input"
              />
              <button className="filter-toggle-btn" onClick={() => setShowFilters(true)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </button>
            </div>
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div className="export-dropdown-menu">
                  <button onClick={() => handleExport('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => handleExport('Print')}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            <button className="control-btn-primary" onClick={() => navigate('/purchases/requisition/add')}>
              <span className="plus-icon">+</span> Add
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="filter-panel card p-3 mb-3">
            <div className="row">
              <div className="col-md-3 mb-2">
                <label>Location</label>
                <select name="location_id" className="form-control" value={filters.location_id} onChange={handleFilterChange}>
                  <option value="">All Locations</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3 mb-2">
                <label>Status</label>
                <select name="status" className="form-control" value={filters.status} onChange={handleFilterChange}>
                  <option value="">All Statuses</option>
                  <option value="ordered">Ordered</option>
                  <option value="partial">Partial</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div className="col-md-3 mb-2">
                <label>Date From</label>
                <input type="date" name="start_date" className="form-control" value={filters.start_date} onChange={handleFilterChange} />
              </div>
              <div className="col-md-3 mb-2">
                <label>Date To</label>
                <input type="date" name="end_date" className="form-control" value={filters.end_date} onChange={handleFilterChange} />
              </div>
              <div className="col-md-3 mb-2">
                <label>Required By From</label>
                <input type="date" name="required_by_start" className="form-control" value={filters.required_by_start} onChange={handleFilterChange} />
              </div>
              <div className="col-md-3 mb-2">
                <label>Required By To</label>
                <input type="date" name="required_by_end" className="form-control" value={filters.required_by_end} onChange={handleFilterChange} />
              </div>
            </div>
            <div className="filter-actions mt-2">
              <button className="btn btn-sm btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-sm btn-primary ml-2" onClick={() => setShowFilters(false)}>Close</button>
            </div>
          </div>
        )}

        {error && <div className="alert alert-danger">{error}</div>}

        {isLoading ? (
          <div className="loading-sales">
            <h3>⏳ Loading Purchase Requisitions...</h3>
            <p>Fetching purchase requisitions data from server...</p>
          </div>
        ) : data.length === 0 ? (
          <div className="no-sales">
            <h3>📊 No Purchase Requisitions Found</h3>
            <p>No purchase requisitions have been recorded yet.</p>
          </div>
        ) : (
          <>
            <div className="products-table-container">
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Requisition No</th>
                    <th>Date</th>
                    <th>Delivery Date</th>
                    <th>Requested By</th>
                    <th>Location</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr key={row.id}>
                      <td className="actions-cell">
                        <div className="action-dropdown-container">
                          <button className="action-btn-trigger" onClick={(e) => toggleDropdown(row.id, e)}>
                            Actions <span className="arrow-down">▼</span>
                          </button>
                          {openActionId === row.id && ReactDOM.createPortal(
                            <div
                              className="action-dropdown-menu"
                              style={{
                                position: 'fixed',
                                top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                left: `${dropdownPosition.left}px`,
                                maxHeight: dropdownPosition.maxHeight === null ? undefined : `${dropdownPosition.maxHeight}px`,
                                zIndex: 9999999
                              }}
                            >
                              <button className="dropdown-item" onClick={() => handleView(row.id)}>👁️ View</button>
                              <button className="dropdown-item delete-item" onClick={() => handleDelete(row.id)}>🗑️ Delete</button>
                            </div>,
                            document.body
                          )}
                        </div>
                      </td>
                      <td>{row.ref_no}</td>
                      <td>{formatDate(row.transaction_date)}</td>
                      <td>{formatDate(row.delivery_date)}</td>
                      <td>{row.added_by || row.requested_by || '-'}</td>
                      <td>{row.location_name}</td>
                      <td>
                        {statusMap[row.status] ? (
                          <span className={statusMap[row.status].className}>{statusMap[row.status].label}</span>
                        ) : (
                          <span className="badge badge-info">{row.status}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
              <div>
                Showing {total === 0 ? 0 : ((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, total)} of {total} entries
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                >
                  Previous
                </button>
                <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem' }}>
                  Page {currentPage} of {lastPage}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= lastPage}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {viewItem && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setViewItem(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999999 }}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '12px', maxWidth: '700px', width: '90%', maxHeight: '90vh', overflowY: 'auto', padding: '1.5rem', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '1rem', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#1e293b' }}>Purchase Requisition Details</h3>
              <button onClick={() => setViewItem(null)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>
            {viewLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>⏳ Loading details...</div>
            ) : (
              <div>
                <table className="products-table" style={{ marginBottom: '1.5rem' }}>
                  <tbody>
                    <tr><td style={{ fontWeight: '600', width: '35%' }}>Requisition No</td><td>{viewItem.ref_no}</td></tr>
                    <tr><td style={{ fontWeight: '600' }}>Date</td><td>{formatDate(viewItem.transaction_date)}</td></tr>
                    <tr><td style={{ fontWeight: '600' }}>Delivery Date</td><td>{formatDate(viewItem.delivery_date)}</td></tr>
                    <tr><td style={{ fontWeight: '600' }}>Location</td><td>{viewItem.location?.name || viewItem.location_name || '-'}</td></tr>
                    <tr><td style={{ fontWeight: '600' }}>Status</td><td><span className="badge badge-info">{viewItem.status}</span></td></tr>
                  </tbody>
                </table>
                {viewItem.purchase_lines && viewItem.purchase_lines.length > 0 && (
                  <>
                    <h4 style={{ fontSize: '1rem', marginBottom: '0.75rem', color: '#334155' }}>Products</h4>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="products-table">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Quantity</th>
                            <th>Secondary Quantity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {viewItem.purchase_lines.map((line) => (
                            <tr key={line.id}>
                              <td style={{ fontWeight: '500' }}>{line.product?.name || line.variations?.product_variation?.name || `Variation #${line.variation_id}`}</td>
                              <td>{line.quantity}</td>
                              <td>{line.secondary_unit_quantity || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
              <button className="pagination-btn" onClick={() => setViewItem(null)}>Close</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PurchaseRequisition;
